import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Package,
  ShoppingCart,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  FileText,
  Image,
  Layers,
  BarChart4,
  MessageSquare,
  Bot,
  ShoppingBag,
  Percent,
  PanelTop,
  GalleryHorizontal,
  LayoutTemplate,
  Link2,
  Truck,
  Shield,
  ShieldCheck,
  ChevronDown,
  Palette,
  Inbox,
  Building2,
  Search,
  Briefcase,
  CreditCard,
  Server,
  HardDrive,
  Container,
  Bell,
  MessagesSquare,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { cn } from '../utils/cn';

interface NavItem {
  icon: React.ReactNode;
  label: string;
  href: string;
  badge?: number;
  /** Parent shown if user has any of these (or any submenu permission). */
  requiredPermission?: string;
  submenu?: NavItem[];
}

function pathMatches(pathname: string, href: string): boolean {
  return pathname === href || (href !== '/' && pathname.startsWith(href + '/')) || pathname.startsWith(href);
}

export function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();

  const navItems: NavItem[] = useMemo(
    () => [
      {
        icon: <BarChart3 className="h-5 w-5" />,
        label: 'داشبورد',
        href: '/dashboard',
      },
      {
        icon: <ShoppingCart className="h-5 w-5" />,
        label: 'سفارش‌ها',
        href: '/orders',
        requiredPermission: 'view_orders',
        submenu: [
          { icon: <ShoppingCart className="h-4 w-4" />, label: 'تمام سفارش‌ها', href: '/orders' },
          { icon: <CreditCard className="h-4 w-4" />, label: 'پرداخت‌ها', href: '/payments' },
        ],
      },
      {
        icon: <Package className="h-5 w-5" />,
        label: 'محصولات',
        href: '/products',
        requiredPermission: 'manage_products',
        submenu: [
          { icon: <Package className="h-4 w-4" />, label: 'تمام محصولات', href: '/products' },
          { icon: <Layers className="h-4 w-4" />, label: 'دسته‌بندی‌ها', href: '/categories' },
          { icon: <BarChart4 className="h-4 w-4" />, label: 'انبار', href: '/inventory' },
          { icon: <BarChart3 className="h-4 w-4" />, label: 'بازدید محصولات', href: '/product-views' },
          { icon: <ShoppingBag className="h-4 w-4" />, label: 'محصولات باسلام', href: '/basalam-products' },
        ],
      },
      {
        icon: <Users className="h-5 w-5" />,
        label: 'مشتریان',
        href: '/customers',
        requiredPermission: 'manage_customers',
        submenu: [
          { icon: <Users className="h-4 w-4" />, label: 'تمام مشتریان', href: '/customers' },
          { icon: <ShoppingCart className="h-4 w-4" />, label: 'سبدهای خرید', href: '/carts' },
        ],
      },
      {
        icon: <Percent className="h-5 w-5" />,
        label: 'تخفیف‌ها',
        href: '/discounts',
        requiredPermission: 'manage_discounts',
      },
      {
        icon: <Palette className="h-5 w-5" />,
        label: 'ظاهر سایت',
        href: '/banners',
        // Visible if any appearance submenu permission matches
        submenu: [
          { icon: <Image className="h-4 w-4" />, label: 'بنرها', href: '/banners', requiredPermission: 'manage_banners' },
          { icon: <PanelTop className="h-4 w-4" />, label: 'هدر سایت', href: '/headers', requiredPermission: 'manage_headers' },
          { icon: <GalleryHorizontal className="h-4 w-4" />, label: 'کروسل‌ها', href: '/carousels', requiredPermission: 'manage_carousels' },
          { icon: <LayoutTemplate className="h-4 w-4" />, label: 'لندینگ‌ها', href: '/landings', requiredPermission: 'manage_landings' },
          { icon: <Link2 className="h-4 w-4" />, label: 'لینک‌های کوتاه', href: '/short-links', requiredPermission: 'manage_short_links' },
        ],
      },
      {
        icon: <FileText className="h-5 w-5" />,
        label: 'محتوا و پیام‌ها',
        href: '/blog',
        requiredPermission: 'manage_blog',
        submenu: [
          { icon: <FileText className="h-4 w-4" />, label: 'مقالات', href: '/blog' },
          { icon: <MessageSquare className="h-4 w-4" />, label: 'نظرات و پرسش‌ها', href: '/reviews' },
          { icon: <Inbox className="h-4 w-4" />, label: 'پیام‌های تماس', href: '/contact-messages' },
          { icon: <Bot className="h-4 w-4" />, label: 'پیام‌های بله', href: '/bale-messages' },
          { icon: <ShoppingBag className="h-4 w-4" />, label: 'پیام‌های باسلام', href: '/basalam-messages' },
        ],
      },
      {
        icon: <Building2 className="h-5 w-5" />,
        label: 'جستجوی کسب‌وکار',
        href: '/business-search',
        requiredPermission: 'manage_leads',
        submenu: [
          { icon: <Search className="h-4 w-4" />, label: 'جاب‌های جستجو', href: '/business-search', requiredPermission: 'manage_leads' },
          { icon: <Users className="h-4 w-4" />, label: 'لیدها', href: '/leads', requiredPermission: 'manage_leads' },
        ],
      },
      {
        icon: <Briefcase className="h-5 w-5" />,
        label: 'هوشمندی شغلی',
        href: '/job-search',
        requiredPermission: 'manage_jobs',
        submenu: [
          { icon: <Search className="h-4 w-4" />, label: 'جستجوی شغل', href: '/job-search', requiredPermission: 'manage_jobs' },
          { icon: <Briefcase className="h-4 w-4" />, label: 'آگهی‌ها', href: '/jobs', requiredPermission: 'manage_jobs' },
        ],
      },
      {
        icon: <Truck className="h-5 w-5" />,
        label: 'نحوه ارسال',
        href: '/shipping',
        requiredPermission: 'manage_shipping',
      },
      {
        icon: <BarChart4 className="h-5 w-5" />,
        label: 'گزارش‌ها',
        href: '/reports',
        requiredPermission: 'view_financial_reports',
      },
      {
        icon: <Server className="h-5 w-5" />,
        label: 'زیرساخت',
        href: '/infrastructure',
        requiredPermission: 'manage_ops',
        submenu: [
          { icon: <Server className="h-4 w-4" />, label: 'نمای کلی', href: '/infrastructure', requiredPermission: 'manage_ops' },
          { icon: <Container className="h-4 w-4" />, label: 'سرویس‌ها', href: '/infrastructure/services', requiredPermission: 'manage_ops' },
          { icon: <HardDrive className="h-4 w-4" />, label: 'ذخیره و پاکسازی', href: '/infrastructure/storage', requiredPermission: 'manage_ops' },
          { icon: <HardDrive className="h-4 w-4" />, label: 'بکاپ', href: '/infrastructure/backups', requiredPermission: 'manage_ops' },
          { icon: <Shield className="h-4 w-4" />, label: 'امنیت', href: '/infrastructure/security', requiredPermission: 'manage_ops' },
          { icon: <Bell className="h-4 w-4" />, label: 'اعلان‌ها', href: '/infrastructure/notifications', requiredPermission: 'manage_ops' },
        ],
      },
      {
        icon: <MessagesSquare className="h-5 w-5" />,
        label: 'Mattermost',
        href: '/mattermost/messages',
        requiredPermission: 'manage_mattermost',
        submenu: [
          { icon: <MessageSquare className="h-4 w-4" />, label: 'پیام‌ها', href: '/mattermost/messages', requiredPermission: 'manage_mattermost' },
          { icon: <MessagesSquare className="h-4 w-4" />, label: 'کانال‌ها', href: '/mattermost/channels', requiredPermission: 'manage_mattermost' },
          { icon: <Search className="h-4 w-4" />, label: 'جستجو', href: '/mattermost/search', requiredPermission: 'manage_mattermost' },
          { icon: <Bell className="h-4 w-4" />, label: 'اعلان‌ها', href: '/mattermost/notifications', requiredPermission: 'manage_mattermost' },
        ],
      },
      {
        icon: <Settings className="h-5 w-5" />,
        label: 'تنظیمات',
        href: '/settings/admins',
        requiredPermission: 'manage_admins',
        submenu: [
          { icon: <Shield className="h-4 w-4" />, label: 'ادمین‌ها', href: '/settings/admins' },
          { icon: <ShieldCheck className="h-4 w-4" />, label: 'نقش‌ها', href: '/settings/roles' },
        ],
      },
    ],
    []
  );

  const filteredNavItems = useMemo(() => {
    return navItems
      .map((item) => {
        if (!item.submenu) {
          if (item.requiredPermission && !hasPermission(item.requiredPermission)) return null;
          return item;
        }

        const submenu = item.submenu.filter(
          (sub) => !sub.requiredPermission || hasPermission(sub.requiredPermission)
        );
        if (submenu.length === 0) return null;

        // Parent with own permission (e.g. محصولات)
        if (item.requiredPermission && !hasPermission(item.requiredPermission)) return null;

        return { ...item, submenu };
      })
      .filter(Boolean) as NavItem[];
  }, [navItems, hasPermission]);

  const isGroupActive = (item: NavItem) => {
    if (item.submenu?.length) {
      return item.submenu.some((sub) => pathMatches(location.pathname, sub.href));
    }
    return pathMatches(location.pathname, item.href);
  };

  useEffect(() => {
    const active = filteredNavItems.find((item) => item.submenu && isGroupActive(item));
    if (active) setExpandedMenu(active.label);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only sync on route change
  }, [location.pathname]);

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 lg:hidden bg-indigo-600 text-white p-3 rounded-full shadow-lg"
      >
        {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      <aside
        className={cn(
          'fixed inset-y-0 right-0 z-40 flex h-screen w-64 flex-col bg-gradient-to-b from-slate-900 to-slate-800 text-white shadow-xl transition-transform duration-300 lg:translate-x-0',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
      >
        <div className="flex-shrink-0 border-b border-slate-700 p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center overflow-hidden">
              <img src="/icon.png" alt="ایوا" className="h-full w-full object-contain p-0.5" />
            </div>
            <div>
              <h1 className="text-lg font-bold">ایوا</h1>
              <p className="text-xs text-slate-400">پنل مدیریت</p>
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 border-b border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-indigo-500 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold">{user?.name.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
        </div>

        <nav className="min-h-0 flex-1 overflow-y-auto p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const active = isGroupActive(item);
            const expanded = expandedMenu === item.label || Boolean(item.submenu && active);
            return (
              <div key={item.label}>
                {item.submenu ? (
                  <button
                    type="button"
                    onClick={() => setExpandedMenu(expandedMenu === item.label ? null : item.label)}
                    className={cn(
                      'flex w-full items-center justify-between px-4 py-2.5 rounded-lg transition-colors duration-200',
                      active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    )}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {item.icon}
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                    <ChevronDown
                      className={cn('h-4 w-4 transition-transform', expanded ? 'rotate-180' : '')}
                    />
                  </button>
                ) : (
                  <Link
                    to={item.href}
                    onClick={() => setIsOpen(false)}
                    className={cn(
                      'flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors duration-200',
                      active ? 'bg-indigo-600 text-white' : 'text-slate-300 hover:bg-slate-700 hover:text-white'
                    )}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {item.icon}
                      <span className="text-sm font-medium">{item.label}</span>
                    </div>
                  </Link>
                )}

                {item.submenu && expanded && (
                  <div className="mt-1 mr-3 space-y-1 border-r border-slate-600 pr-3">
                    {item.submenu.map((subitem) => (
                      <Link
                        key={subitem.href}
                        to={subitem.href}
                        onClick={() => setIsOpen(false)}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors duration-200',
                          pathMatches(location.pathname, subitem.href)
                            ? 'bg-indigo-500/80 text-white'
                            : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/60'
                        )}
                      >
                        {subitem.icon}
                        {subitem.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="flex-shrink-0 border-t border-slate-700 p-4">
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors duration-200"
          >
            <LogOut className="h-5 w-5" />
            <span className="text-sm font-medium">خروج</span>
          </button>
        </div>
      </aside>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setIsOpen(false)} />
      )}
    </>
  );
}
